import { useCallback, useEffect, useMemo, useState } from "react";
import {
  createDeviceType,
  deleteDeviceType,
  listDeviceTypes,
  updateDeviceType,
} from "../../api/endpoints/deviceTypes.js";
import { filterBySearch } from "../services/entityFilters.js";

export function useDeviceTypes() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listDeviceTypes();
      setItems(data);
    } catch (err) {
      setError(err.message ?? "Failed to load device types");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const visible = useMemo(
    () => filterBySearch(items, search, ["name", "manufacturer", "protocol", "status"]),
    [items, search],
  );

  const openCreate = useCallback(() => {
    setEditing(null);
    setModalOpen(true);
  }, []);

  const openEdit = useCallback((deviceType) => {
    setEditing(deviceType);
    setModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setEditing(null);
  }, []);

  const saveDeviceType = useCallback(async (payload) => {
    setSaving(true);
    setError(null);
    try {
      if (editing) {
        const updated = await updateDeviceType(editing.id, payload);
        setItems((prev) => prev.map((row) => (row.id === editing.id ? updated : row)));
      } else {
        const created = await createDeviceType(payload);
        setItems((prev) => [...prev, created]);
      }
      closeModal();
    } catch (err) {
      setError(err.message ?? "Failed to save device type");
      throw err;
    } finally {
      setSaving(false);
    }
  }, [editing, closeModal]);

  const removeDeviceType = useCallback(async (deviceTypeId) => {
    setSaving(true);
    setError(null);
    try {
      await deleteDeviceType(deviceTypeId);
      setItems((prev) => prev.filter((row) => row.id !== deviceTypeId));
    } catch (err) {
      setError(err.message ?? "Failed to delete device type");
      throw err;
    } finally {
      setSaving(false);
    }
  }, []);

  return {
    items: visible,
    allItems: items,
    loading,
    saving,
    error,
    search,
    setSearch,
    modalOpen,
    editing,
    openCreate,
    openEdit,
    closeModal,
    saveDeviceType,
    removeDeviceType,
    reload,
    total: items.length,
  };
}
